import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, TextField, Tooltip } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config.js";

import { READ_DEPARTMENT_BY_ID } from "../../graph/departments/queries.js";
import {
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
} from "../../graph/departments/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);

const validationSchema = yup.object().shape({
  name: yup.string().trim().required("Department name is required"),
  description: yup.string().nullable(),
});

const EditDepartmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);

  const redirectPath = location.state?.redirectPath || "/manageDepartments";

  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        const res = await client.request(READ_DEPARTMENT_BY_ID, { id });

        const department = res?.readDepartmentById;

        if (!department) {
          throw new Error("Department not found");
        }

        setInitialValues({
          name: department.name || "",
          description: department.description || "",
          tenantId: department.tenantId || "",
        });
      } catch (err) {
        console.error("Error fetching department:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load department",
        });
      }
    };

    fetchDepartment();
  }, [id, setSnackbar]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this department?")) {
      return;
    }

    try {
      await client.request(DELETE_DEPARTMENT, { id: String(id) });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Department deleted successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Delete failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete department",
      });
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      await client.request(UPDATE_DEPARTMENT, {
        input: {
          id,
          name: values.name.trim(),
          description: values.description?.trim() || null,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Department updated successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Update failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update department",
      });
    }
  };

  if (!initialValues) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT DEPARTMENT"
          subtitle={`Edit department: ${initialValues.name}`}
        />

        <Tooltip title="Delete department">
          <ActionButton
            tone="danger"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete Department
          </ActionButton>
        </Tooltip>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                label="Department Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <input type="hidden" name="tenantId" value={values.tenantId || ""} />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditDepartmentForm;